import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import api, { API, formatApiError } from "../../lib/api";
import GatewayApiDocs from "./gateway/ApiDocs";
import { TablePager, useClientPage } from "../../components/sc/Pager";

const INK = "#14101F";
const VIOLET = "#4931E8";
const LIME = "#C7F23D";

export default function Developers() {
  const { upiIds } = useOutletContext();
  const [keys, setKeys] = useState([]);
  const { slice: keyPage, pager: keyPager } = useClientPage(keys, 10);
  const [newLabel, setNewLabel] = useState("");
  const [newMode, setNewMode] = useState("live"); // test | live
  const [working, setWorking] = useState(false);
  // The full secret is returned ONLY once, at creation — held in memory so the
  // user can copy it, then dropped (never re-fetchable; DB stores only a hash).
  const [freshKey, setFreshKey] = useState(null); // {id,key_id,secret,mode,label}
  const [showSecret, setShowSecret] = useState(false); // fresh-secret reveal
  const [copyKey, setCopyKey] = useState("copy");
  const [copyCurl, setCopyCurl] = useState("copy");

  const defaultVpa = upiIds.find((u) => u.is_default)?.vpa || "yourvpa@bank";
  const apiBase = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/v1`;

  const load = async () => {
    const { data } = await api.get("/api-keys");
    setKeys(data);
  };

  useEffect(() => {
    load();
  }, []);

  // Create a NEW key (merchants can hold many, one per use case). Never deletes
  // an existing key — that's a separate, explicit revoke.
  const createKey = async () => {
    const label = newLabel.trim();
    if (!label) { toast.error("Give the key a name (e.g. “Website checkout”)"); return; }
    setWorking(true);
    try {
      const { data } = await api.post("/api-keys", { mode: newMode, label });
      setFreshKey(data);
      setShowSecret(true);
      setNewLabel("");
      await load();
      toast.success("Key created — copy the secret now, it won't be shown again");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setWorking(false);
    }
  };

  const revokeKey = async (k) => {
    if (!window.confirm(`Revoke “${k.label || k.key_id}”? Any integration using this key stops working immediately. This can't be undone.`)) return;
    try {
      await api.delete(`/api-keys/${k.id}`);
      if (freshKey?.id === k.id) setFreshKey(null);
      await load();
      toast.success("Key revoked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const maskSecret = (s) => (s ? `${s.slice(0, 8)}${"•".repeat(24)}${s.slice(-4)}` : "");
  const dismissFresh = () => { setFreshKey(null); setShowSecret(false); };

  const copy = async (text, which) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      const setter = which === "key" ? setCopyKey : setCopyCurl;
      setter("copied ✦");
      setTimeout(() => setter("copy"), 1400);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const curl = `curl ${apiBase}/orders \\
  -H "Authorization: Bearer ${freshKey?.secret || "sk_live_***"}" \\
  -H "Content-Type: application/json" \\
  -d '{"amount":500,"payment_mode":"p2m","receipt":"inv#1","customer":{"name":"Rohan Mehta","phone":"+919876543210"}}'`;

  return (
    <div data-testid="developers-page" className="ui-page">
      <div className="dev-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <section className="ui-card" data-testid="api-keys-card">
            <header className="ui-card-head">
              <div style={{ minWidth: 0, flex: "1 1 240px" }}>
                <h3 className="ui-card-title">API keys</h3>
                <p className="ui-card-sub" style={{ lineHeight: 1.5 }}>Create a separate key per integration (website, POS, billing) so you can revoke one without breaking the others. Use <strong>test</strong> keys while building, <strong>live</strong> in production.</p>
              </div>
            </header>
            <div className="ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="dev-base">
                <span className="ui-eyebrow" style={{ flex: "none" }}>Base URL</span>
                <span className="ui-mono" style={{ color: VIOLET, fontWeight: 700, fontSize: 12, wordBreak: "break-all", minWidth: 0 }}>{apiBase}</span>
              </div>

              {/* CREATE */}
              <div className="dev-panel" data-testid="key-create-form">
                <span className="ui-eyebrow">Create a new key</span>
                <div className="dev-create-row">
                  <input
                    data-testid="key-new-label"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value.slice(0, 40))}
                    onKeyDown={(e) => { if (e.key === "Enter") createKey(); }}
                    placeholder="Key name — e.g. Website checkout"
                    className="ui-input"
                    style={{ flex: "1 1 180px", minWidth: 0 }}
                  />
                  <div className="ui-tabs" role="group" aria-label="Key mode" style={{ flex: "none", boxShadow: "none", padding: 3 }}>
                    {["test", "live"].map((m) => (
                      <button key={m} data-testid={`key-mode-${m}`} onClick={() => setNewMode(m)} aria-pressed={newMode === m} className="ui-tab"
                        style={{ background: newMode === m ? VIOLET : "transparent", color: newMode === m ? "#fff" : INK, textTransform: "capitalize", minHeight: 32 }}>{m}</button>
                    ))}
                  </div>
                </div>
                <button data-testid="key-create" onClick={createKey} disabled={working} className="ui-btn ui-btn-primary" style={{ width: "100%", fontSize: 14, minHeight: 44 }}>
                  {working ? "Creating…" : "+ Create key"}
                </button>
              </div>

              {/* FRESH SECRET — shown exactly once */}
              {freshKey && (
                <div data-testid="key-fresh" className="dev-panel" style={{ background: "#FFF9E6", borderColor: "#9a6a00" }}>
                  <div>
                    <div className="ui-mono" style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: "#7a5400", marginBottom: 4, overflowWrap: "anywhere" }}>⚠ Secret for “{freshKey.label}” — shown only once</div>
                    <p style={{ fontSize: 12, color: "#7a5400", margin: 0, lineHeight: 1.45 }}>Copy it now and store it somewhere safe. We can't show it again.</p>
                  </div>
                  <div className="dev-secret-row">
                    <div className="sc-scroll dev-secret" data-testid="key-secret">
                      {showSecret ? freshKey.secret : maskSecret(freshKey.secret)}
                    </div>
                    <div className="dev-secret-btns">
                      <button data-testid="key-toggle-secret" onClick={() => setShowSecret((v) => !v)} className="ui-btn ui-btn-sm" style={{ minHeight: 38 }}>{showSecret ? "hide" : "reveal"}</button>
                      <button data-testid="key-copy" onClick={() => copy(freshKey.secret, "key")} className="ui-btn ui-btn-primary ui-btn-sm" style={{ minHeight: 38 }}>{copyKey}</button>
                    </div>
                  </div>
                  <div>
                    <button data-testid="key-fresh-done" onClick={dismissFresh} className="ui-btn ui-btn-sm" style={{ minHeight: 38 }}>✓ I've saved it</button>
                  </div>
                </div>
              )}

              {/* KEY LIST */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="key-list">
                {keys.length === 0 ? (
                  <div className="ui-empty" style={{ padding: "18px 8px", fontSize: 13 }} data-testid="key-empty">No API keys yet — create one above.</div>
                ) : keyPage.map((k) => (
                  <div key={k.id} data-testid={`key-row-${k.id}`} className="dev-key" style={{ background: k.mode === "live" ? "#FBFAF7" : "#fff" }}>
                    <div className="ui-between" style={{ flexWrap: "nowrap" }}>
                      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="ui-trunc" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14.5 }}>{k.label || "Untitled key"}</span>
                        <span className="ui-badge" style={{ flex: "none", textTransform: "uppercase", fontSize: 9.5, padding: "2px 7px", color: k.mode === "live" ? "#137a47" : "#7a5400", background: k.mode === "live" ? "#DFF7E7" : "#FFF3D6" }}>{k.mode}</span>
                      </div>
                      <button data-testid={`key-revoke-${k.id}`} onClick={() => revokeKey(k)} title="Revoke key" className="ui-btn ui-btn-danger ui-btn-sm" style={{ flex: "none", minHeight: 36 }}>Revoke</button>
                    </div>
                    <div className="dev-keyid">
                      <span className="sc-scroll" style={{ flex: 1, minWidth: 0, fontFamily: "JetBrains Mono, monospace", fontSize: 12, overflowX: "auto", whiteSpace: "nowrap", color: "#51495f" }}>{k.key_id}</span>
                      <button onClick={() => copy(k.key_id, "keyid")} title="Copy key ID" className="ui-btn ui-btn-sm" style={{ flex: "none", boxShadow: "none", minHeight: 30 }}>copy</button>
                    </div>
                    <div className="ui-between ui-mono ui-faint" style={{ fontSize: 11, gap: 6 }}>
                      <span style={{ overflowWrap: "anywhere" }}>secret {k.secret_preview || "sk_••••"}</span>
                      <span>{k.created_at ? new Date(k.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
              <TablePager testId="keys-pager" {...keyPager} />
            </div>
          </section>

          <section className="ui-card" data-testid="integration-guide-card">
            <header className="ui-card-head">
              <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                <h3 className="ui-card-title">Integration guide (PDF)</h3>
                <p className="ui-card-sub" style={{ lineHeight: 1.5 }}>Everything your developer needs in one document: orders, P2M / P2P / Auto Pay, redirects, webhooks with signature checks, payouts, errors and a go-live checklist.</p>
              </div>
              <a data-testid="integration-guide-pdf" className="ui-btn ui-btn-primary" href={`${API}/api-keys/integration-guide.pdf`} download="supercash-integration-guide.pdf">
                <FileDown size={16} /> Download PDF
              </a>
            </header>
          </section>
          <section className="ui-card">
            <header className="ui-card-head"><h3 className="ui-card-title">How the gateway works</h3></header>
            <div className="ui-card-pad">
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: "#51495f", margin: 0, overflowWrap: "anywhere" }}>
                Create an <Code>order</Code>, send the customer to its <Code>checkout_url</Code>, and they pay in their UPI app. Track it with <Code>GET /v1/orders/&#123;id&#125;</Code>, <Code>GET /v1/payments/&#123;id&#125;</Code>, or the <Code>order.paid</Code> / <Code>payment.success</Code> webhooks. Money settles straight into your account — never routed through us.
              </p>
              <p className="ui-muted" style={{ fontSize: 12.5, margin: "12px 0 0" }}>
                Using the payment gateway (payins &amp; payouts)? <a href="#gateway-api" className="ui-link">See the Gateway API reference ↓</a>
              </p>
            </div>
          </section>
        </div>

        <div className="dev-curl" data-testid="curl-card">
          <div className="ui-between" style={{ marginBottom: 14, flexWrap: "nowrap" }}>
            <span className="ui-mono" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: LIME }}>Create an order</span>
            <button data-testid="curl-copy" onClick={() => copy(curl, "curl")} className="dev-curl-btn">{copyCurl}</button>
          </div>
          <pre className="sc-scroll dev-pre">
            <span style={{ color: "#8a84a0" }}># request{"\n"}</span>
            <span style={{ color: LIME }}>curl</span> {apiBase}/orders \{"\n"}
            {"  "}-H <span style={{ color: "#9fd4ff" }}>"Authorization: Bearer {freshKey?.secret || "sk_live_***"}"</span> \{"\n"}
            {"  "}-H <span style={{ color: "#9fd4ff" }}>"Content-Type: application/json"</span> \{"\n"}
            {"  "}-d <span style={{ color: "#9fd4ff" }}>{"'{"}"amount":500,"payment_mode":"p2m","receipt":"inv#1","customer":{"{"}"name":"Rohan Mehta","phone":"+919876543210"{"}}'"}</span>
          </pre>
          <p style={{ fontSize: 12.5, color: "#b6b0c8", margin: "12px 0 0", lineHeight: 1.6, overflowWrap: "anywhere" }}>
            <Code dark>payment_mode</Code>: <Code dark>p2m</Code> (any UPI app) or <Code dark>p2p</Code> (PhonePe/Paytm to a personal VPA). Required <Code dark>customer.name</Code> and <Code dark>customer.phone</Code> are stored on the order so you can reconcile exactly who paid — they surface in the dashboard, transactions &amp; CSV and the <Code dark>payment.success</Code> webhook, even when the bank notification carries no name. Redirect the customer to <Code dark>checkout_url</Code>; poll <Code dark>GET /v1/orders/&#123;id&#125;</Code> or listen for the <Code dark>order.paid</Code> webhook.
          </p>
          <div style={{ height: 2, background: "#2a2540", margin: "18px 0" }} />
          <span className="ui-mono" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: LIME }}>Response</span>
          <pre className="sc-scroll dev-pre" style={{ marginTop: 10 }}>{`{
  "id": "ord_ab12cd",
  "entity": "order",
  "amount": 500,
  "currency": "INR",
  "status": "created",
  "payment_mode": "p2m",
  "checkout_url": "https://supercash.one/pay_id/ab12cd"
}`}</pre>
        </div>
      </div>

      <div id="gateway-api" style={{ scrollMarginTop: 16, minWidth: 0 }}>
        <GatewayApiDocs />
      </div>

      <style>{`
        .dev-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; align-items: start; }
        .dev-base { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; background: var(--ui-surface-2); border: 2px solid var(--ui-line); border-radius: 10px; padding: 9px 12px; }
        .dev-panel { display: flex; flex-direction: column; gap: 10px; background: #FBFAF7; border: 2px solid ${INK}; border-radius: 14px; padding: 14px; min-width: 0; }
        .dev-create-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .dev-secret-row { display: flex; gap: 8px; min-width: 0; }
        .dev-secret { flex: 1; min-width: 0; background: #fff; border: 2px solid ${INK}; border-radius: 10px; padding: 10px 12px; font-family: "JetBrains Mono", monospace; font-size: 12.5px; overflow-x: auto; white-space: nowrap; }
        .dev-secret-btns { display: flex; gap: 8px; flex: none; }
        .dev-key { border: 2px solid ${INK}; border-radius: 12px; padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
        .dev-keyid { display: flex; gap: 8px; align-items: center; background: #fff; border: 2px solid #ECE7DB; border-radius: 9px; padding: 5px 6px 5px 10px; min-width: 0; }
        .dev-curl { background: ${INK}; color: #fff; border: 2.5px solid ${INK}; border-radius: 18px; box-shadow: 5px 5px 0 ${VIOLET}; padding: 22px; min-width: 0; position: sticky; top: 16px; }
        .dev-curl-btn { cursor: pointer; background: #221d36; color: #fff; border: 2px solid #3a3550; border-radius: 9px; font-family: "JetBrains Mono", monospace; font-size: 11px; font-weight: 700; padding: 0 12px; min-height: 36px; flex: none; }
        .dev-curl-btn:hover { border-color: ${LIME}; }
        .dev-pre { margin: 0; overflow-x: auto; font-family: "JetBrains Mono", monospace; font-size: 12.5px; line-height: 1.7; white-space: pre; max-width: 100%; background: #1b1630; border: 2px solid #2a2540; border-radius: 12px; padding: 12px 14px; }
        @media (max-width: 980px) {
          .dev-grid { grid-template-columns: minmax(0, 1fr); }
          .dev-curl { position: static; }
        }
        @media (max-width: 640px) {
          .dev-secret-row { flex-direction: column; }
          .dev-secret-btns { width: 100%; }
          .dev-secret-btns button { flex: 1; }
          .dev-curl { padding: 16px; border-radius: 16px; }
          .dev-pre { font-size: 11.5px; }
        }
      `}</style>
    </div>
  );
}

function Code({ children, dark }) {
  return <span style={{ fontFamily: "JetBrains Mono, monospace", background: dark ? "#2a2540" : "#ECE8FB", color: dark ? "#e6e1ff" : undefined, padding: "1px 5px", borderRadius: 4 }}>{children}</span>;
}
